import React, { useContext, useEffect, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { DateInput } from 'semantic-ui-calendar-react';
import { Button, Dropdown, Form, Message, Pagination } from 'semantic-ui-react';

import classNames from 'classnames';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';

import Modal from '../Modal';

import ClassroomApi from '../../api/ClassroomApi';

import { dateFormat } from '../../utils';

import '../../css/GradebookPlacementAddToClassroomModal.less';


const PAGE_SIZE = 10;

const CLASSROOM_TABLE_ID = 'placement-add-to-classroom-table';
const TEACHER_TABLE_ID = 'placement-select-teacher-table';

// Fixed filter passed for every teacher fetch; excluded when forwarding user-entered filters to loadTeachers
const USER_TYPE_TEACHER_FILTER = { field: 'userType', operator: 'eq', value: 'teacher_user' };

const STEP = { SELECT: 1, CONFIRM: 2, SELECT_TEACHER: 3, CREATE_CLASS: 4 };

const GradebookPlacementAddToClassroomModal = observer((props) => {
  const {
    onClose,
    open,
    t,
  } = props;

  const {
    adminClassroomManager,
    adminUsersManager,
    classroomManager,
    filteredHeaderTableManager,
    gradebookManager,
    userManager,
  } = useContext(MobXProviderContext);

  const isAdmin = userManager.hasDistrictAdminPermission || userManager.hasSchoolAdminPermission;

  // useRef ensures these are resolved only once; SatCoreComponent returns a new
  // anonymous class on every call, so calling it during render would cause React
  // to unmount/remount the component on every re-render (e.g. closing filter popups).
  const FilteredHeaderTable = useRef(SatCoreComponent('FilteredHeaderTable')).current;
  const ModalBanner = useRef(SatCoreComponent('ModalBanner')).current;
  const SatCoreLoader = useRef(SatCoreComponent('SatCoreLoader')).current;

  /* ── Step 1: classroom selection ── */
  const [step, setStep] = useState(STEP.SELECT);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedClassroomId, setSelectedClassroomId] = useState(null);
  const [selectedClassroom, setSelectedClassroom] = useState(null);

  /* ── Step 3: teacher selection ── */
  const [teacherPage, setTeacherPage] = useState(1);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  /* ── Step 4: create class form ── */
  const [createMode, setCreateMode] = useState('forTeacher'); // 'forTeacher' | 'forSelf'
  const [className, setClassName] = useState('');
  const [classInstitutionId, setClassInstitutionId] = useState('');
  const [classStartDate, setClassStartDate] = useState('');
  const [classEndDate, setClassEndDate] = useState('');
  const [classFormErrors, setClassFormErrors] = useState({});

  /* ── Step 2: confirm / submit ── */
  const [confirmMode, setConfirmMode] = useState('add'); // 'add' | 'create'
  const [confirmBackStep, setConfirmBackStep] = useState(STEP.SELECT);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { classroomsArray, loaded: classroomsLoaded, totalPages: classroomTotalPages } = adminClassroomManager;
  const { users: teacherUsers, loaded: teachersLoaded, totalPages: teacherTotalPages } = adminUsersManager;
  const {
    placementClassrooms,
    placementClassroomTotalPages,
    placementClassroomsLoaded,
  } = classroomManager;

  const displayedClassrooms = isAdmin ? classroomsArray : placementClassrooms;
  const displayedTotalPages = isAdmin ? classroomTotalPages : placementClassroomTotalPages;
  const displayedClassroomsLoaded = isAdmin ? classroomsLoaded : placementClassroomsLoaded;

  const checkedInstances = Array.from(
    gradebookManager.assignmentToRepInstanceBulkCheckedMap.values()
  );

  /* ── loaders ── */
  const loadClassrooms = async (page = 1, filters = []) => {
    const tableData = filteredHeaderTableManager.getTableData(CLASSROOM_TABLE_ID);
    const col = tableData?.column || 'classroomName';
    const dir = tableData?.direction === 'descending' ? 'desc' : 'asc';

    if (isAdmin) {
      await adminClassroomManager.fetchClassrooms(page, PAGE_SIZE, col, dir, filters);
    } else {
      // Map frontend column names to the field names the filtered teacher classrooms API expects
      const TEACHER_SORT_FIELD_MAP = {
        classroomName: 'name',
        classroomStartDate: 'startDate',
        classroomEndDate: 'endDate',
        leadTeacherFirstName: 'leadTeacher.firstName',
        leadTeacherLastName: 'leadTeacher.lastName',
      };
      await classroomManager.fetchTeacherClassroomsForPlacement({
        page: page - 1,
        pageSize: PAGE_SIZE,
        filters,
        sortColumn: TEACHER_SORT_FIELD_MAP[col] || 'name',
        sortDirection: dir,
      });
    }
  };

  const loadTeachers = async (page = 1, userFilters = []) => {
    const tableData = filteredHeaderTableManager.getTableData(TEACHER_TABLE_ID);
    const col = tableData?.column || 'lastName';
    const dir = tableData?.direction || 'ascending';
    // User-entered filters MUST come first so they receive Kendo indices 0, 1, …
    // USER_TYPE_TEACHER_FILTER is handled by the manager as &userType=… (not a Kendo index entry)
    // so placing it at the end prevents a gap at index 0 that would make Kendo ignore all filters.
    const allFilters = [...userFilters.filter((f) => f.field !== 'userType'), USER_TYPE_TEACHER_FILTER];
    await adminUsersManager.getUsersByInstitution(null, page, col, dir, allFilters);
  };

  useEffect(() => {
    if (open) {
      filteredHeaderTableManager.registerTable(CLASSROOM_TABLE_ID, 1, 'classroomName', 'ascending');
      filteredHeaderTableManager.registerTable(TEACHER_TABLE_ID, 1, 'lastName', 'ascending');

      setStep(STEP.SELECT);
      setSelectedClassroomId(null);
      setSelectedClassroom(null);
      setCurrentPage(1);
      setSelectedTeacherId(null);
      setSelectedTeacher(null);
      setTeacherPage(1);
      setClassName('');
      setClassInstitutionId('');
      setClassStartDate('');
      setClassEndDate('');
      setClassFormErrors({});
      setSubmitError(null);
      loadClassrooms(1, []);
    } else {
      filteredHeaderTableManager.clearAll();
    }
  }, [open]);

  /* ── Step 1 handlers ── */
  const handleClassroomPageChange = (_e, { activePage }) => {
    const tableData = filteredHeaderTableManager.getTableData(CLASSROOM_TABLE_ID);
    filteredHeaderTableManager.setActivePage(CLASSROOM_TABLE_ID, activePage);
    setCurrentPage(activePage);
    loadClassrooms(activePage, tableData?.filters || []);
  };

  const handleClassroomSort = async (sortColumn, filters) => {
    const tableData = filteredHeaderTableManager.getTableData(CLASSROOM_TABLE_ID);
    if (!tableData) return;
    if (sortColumn) {
      const newDir = tableData.direction === 'ascending' ? 'descending' : 'ascending';
      filteredHeaderTableManager.setSortColumn(CLASSROOM_TABLE_ID, sortColumn);
      filteredHeaderTableManager.setSortDirection(CLASSROOM_TABLE_ID, newDir);
      filteredHeaderTableManager.setFilters(CLASSROOM_TABLE_ID, filters);
      filteredHeaderTableManager.setActivePage(CLASSROOM_TABLE_ID, 1);
      setCurrentPage(1);
      await loadClassrooms(1, filters);
    } else {
      filteredHeaderTableManager.setFilters(CLASSROOM_TABLE_ID, filters);
      await loadClassrooms(currentPage, filters);
    }
  };

  const handleClassroomFilter = async (filters) => {
    const tableData = filteredHeaderTableManager.getTableData(CLASSROOM_TABLE_ID);
    if (!tableData) return;
    filteredHeaderTableManager.setFilters(CLASSROOM_TABLE_ID, filters);
    filteredHeaderTableManager.setActivePage(CLASSROOM_TABLE_ID, 1);
    setCurrentPage(1);
    await loadClassrooms(1, filters);
  };

  const handleNext = () => {
    const classroom = displayedClassrooms.find((c) => c.classroomId === selectedClassroomId);
    setSelectedClassroom(classroom);
    setConfirmMode('add');
    setConfirmBackStep(STEP.SELECT);
    setSubmitError(null);
    setStep(STEP.CONFIRM);
  };

  const handleResetClassroomFilters = async () => {
    filteredHeaderTableManager.setFilters(CLASSROOM_TABLE_ID, []);
    filteredHeaderTableManager.setSortColumn(CLASSROOM_TABLE_ID, 'classroomName');
    filteredHeaderTableManager.setSortDirection(CLASSROOM_TABLE_ID, 'ascending');
    filteredHeaderTableManager.setActivePage(CLASSROOM_TABLE_ID, 1);
    setCurrentPage(1);
    await loadClassrooms(1, []);
  };

  const handleResetTeacherFilters = async () => {
    filteredHeaderTableManager.setFilters(TEACHER_TABLE_ID, []);
    filteredHeaderTableManager.setSortColumn(TEACHER_TABLE_ID, 'lastName');
    filteredHeaderTableManager.setSortDirection(TEACHER_TABLE_ID, 'ascending');
    filteredHeaderTableManager.setActivePage(TEACHER_TABLE_ID, 1);
    setTeacherPage(1);
    await loadTeachers(1, []);
  };

  const handleAddClassroom = () => {
    setClassName('');
    setClassInstitutionId(userManager.institutionId || '');
    setClassStartDate('');
    setClassEndDate('');
    setClassFormErrors({});
    if (isAdmin) {
      setCreateMode('forTeacher');
      setSelectedTeacherId(null);
      setTeacherPage(1);
      filteredHeaderTableManager.registerTable(TEACHER_TABLE_ID, 1, 'lastName', 'ascending');
      loadTeachers(1, []);
      setStep(STEP.SELECT_TEACHER);
    } else {
      setCreateMode('forSelf');
      setStep(STEP.CREATE_CLASS);
    }
  };

  /* ── Step 3 handlers ── */
  const handleTeacherPageChange = (_e, { activePage }) => {
    const tableData = filteredHeaderTableManager.getTableData(TEACHER_TABLE_ID);
    filteredHeaderTableManager.setActivePage(TEACHER_TABLE_ID, activePage);
    setTeacherPage(activePage);
    const userFilters = (tableData?.filters || []).filter((f) => f.field !== 'userType');
    loadTeachers(activePage, userFilters);
  };

  const handleTeacherSort = async (sortColumn, filters) => {
    const tableData = filteredHeaderTableManager.getTableData(TEACHER_TABLE_ID);
    if (!tableData) return;
    const userFilters = (filters || []).filter((f) => f.field !== 'userType');
    if (sortColumn) {
      const newDir = tableData.direction === 'ascending' ? 'descending' : 'ascending';
      filteredHeaderTableManager.setSortColumn(TEACHER_TABLE_ID, sortColumn);
      filteredHeaderTableManager.setSortDirection(TEACHER_TABLE_ID, newDir);
      filteredHeaderTableManager.setFilters(TEACHER_TABLE_ID, filters);
      filteredHeaderTableManager.setActivePage(TEACHER_TABLE_ID, 1);
      setTeacherPage(1);
      await loadTeachers(1, userFilters);
    } else {
      filteredHeaderTableManager.setFilters(TEACHER_TABLE_ID, filters);
      await loadTeachers(teacherPage, userFilters);
    }
  };

  const handleTeacherFilter = async (filters) => {
    const tableData = filteredHeaderTableManager.getTableData(TEACHER_TABLE_ID);
    if (!tableData) return;
    filteredHeaderTableManager.setFilters(TEACHER_TABLE_ID, filters);
    filteredHeaderTableManager.setActivePage(TEACHER_TABLE_ID, 1);
    setTeacherPage(1);
    const userFilters = (filters || []).filter((f) => f.field !== 'userType');
    await loadTeachers(1, userFilters);
  };

  const handleTeacherBack = () => setStep(STEP.SELECT);

  const handleTeacherNext = () => {
    const teacher = teacherUsers.find((u) => u.id === selectedTeacherId);
    setSelectedTeacher(teacher || null);
    setCreateMode('forTeacher');
    setClassName('');
    setClassInstitutionId(userManager.institutionId || '');
    setClassStartDate('');
    setClassEndDate('');
    setClassFormErrors({});
    setStep(STEP.CREATE_CLASS);
  };

  /* ── Step 4 handlers ── */
  const handleCreateClassBack = () => setStep(createMode === 'forSelf' ? STEP.SELECT : STEP.SELECT_TEACHER);

  const handleCreateClassSubmit = () => {
    const errors = {};
    if (!className.trim()) errors.className = true;
    if (!classStartDate) errors.classStartDate = true;
    if (!classEndDate) errors.classEndDate = true;
    if (Object.keys(errors).length) {
      setClassFormErrors(errors);
      return;
    }
    setConfirmMode('create');
    setConfirmBackStep(STEP.CREATE_CLASS);
    setSubmitError(null);
    setStep(STEP.CONFIRM);
  };

  /* ── Step 2 handlers ── */
  const handleBack = () => {
    setSubmitError(null);
    setStep(confirmBackStep);
  };

  const handleOk = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const studentIds = checkedInstances.map((i) => i.userId).filter(Boolean);
      if (confirmMode === 'create') {
        const response = createMode === 'forSelf'
          ? await classroomManager.createClassroomForUserWithStudents({
            classroomName: className.trim(),
            startDate: classStartDate,
            endDate: classEndDate,
            institutionId: classInstitutionId || undefined,
            studentIds,
          })
          : await classroomManager.createClassroomForTeacherWithStudents({
            teacherId: selectedTeacherId,
            classroomName: className.trim(),
            startDate: classStartDate,
            endDate: classEndDate,
            institutionId: classInstitutionId || undefined,
            studentIds,
          });
        if (response?.status === 'SUCCESS') {
          onClose();
        } else {
          const msg = response?.statusMessage;
          if (msg === 'classNameError') {
            setSubmitError(t('placementCreateClassNameError', 'That class name is already in use. Please choose another.'));
          } else if (msg === 'classDateError') {
            setSubmitError(t('placementCreateClassDateError', 'End date cannot be before start date.'));
          } else {
            setSubmitError(t('placementCreateClassError', 'Failed to create classroom. Please try again.'));
          }
        }
      } else {
        const success = await ClassroomApi.addStudents(selectedClassroomId, studentIds);
        if (success) {
          onClose();
        } else {
          setSubmitError(t('placementAddToClassroomSubmitError', 'Failed to add students to classroom. Please try again.'));
        }
      }
    } catch (e) {
      setSubmitError(t('placementAddToClassroomSubmitError', 'Failed to add students to classroom. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  /* ── table data builders ── */
  const getClassroomHeaderData = () => {
    const headers = [
      { label: t('placementSelectColSelect', 'Select'), filterKey: '', sortKey: '' },
      {
        filterAutoCompleteCallback: null,
        filterComparator: 'contains',
        filterKey: 'classroomName',
        label: t('placementAddToClassroomColClassName', 'Class Name'),
        sortKey: 'classroomName',
      },
      {
        filterAutoCompleteCallback: null,
        filterComparator: 'eq',
        filterKey: 'classroomStartDate',
        label: t('placementAddToClassroomColStartDate', 'Start Date'),
        sortKey: 'classroomStartDate',
        utilizeDateComparatorFilterOptions: 'relational',
      },
      {
        filterAutoCompleteCallback: null,
        filterComparator: 'eq',
        filterKey: 'classroomEndDate',
        label: t('placementAddToClassroomColEndDate', 'End Date'),
        sortKey: 'classroomEndDate',
        utilizeDateComparatorFilterOptions: 'relational',
      },
      {
        filterAutoCompleteCallback: null,
        filterComparator: 'contains',
        filterKey: 'leadTeacherFirstName',
        label: t('placementAddToClassroomColTeacherFirstName', 'Teacher First Name'),
        sortKey: 'leadTeacherFirstName',
      },
      {
        filterAutoCompleteCallback: null,
        filterComparator: 'contains',
        filterKey: 'leadTeacherLastName',
        label: t('placementAddToClassroomColTeacherLastName', 'Teacher Last Name'),
        sortKey: 'leadTeacherLastName',
      },
    ];
    return headers;
  };

  const getClassroomBodyData = () => displayedClassrooms.map((classroom) => {
    const isSelected = selectedClassroomId === classroom.classroomId;
    const selectCell = {
      columnName: 'select',
      columnOnClick: () => setSelectedClassroomId(classroom.classroomId),
      // eslint-disable-next-line react/react-in-jsx-scope
      columnText: <input readOnly checked={isSelected} type='radio' />,
    };
    const rowCells = [
      selectCell,
      {
        columnName: 'classroomName',
        columnOnClick: () => setSelectedClassroomId(classroom.classroomId),
        columnText: classroom.classroomName || '',
      },
      {
        columnName: 'classroomStartDate',
        columnOnClick: () => setSelectedClassroomId(classroom.classroomId),
        columnText: dateFormat(classroom.classroomStartDate, 'twoDigitDate') || '',
      },
      {
        columnName: 'classroomEndDate',
        columnOnClick: () => setSelectedClassroomId(classroom.classroomId),
        columnText: dateFormat(classroom.classroomEndDate, 'twoDigitDate') || '',
      },
    ];
    rowCells.push(
      {
        columnName: 'leadTeacherFirstName',
        columnOnClick: () => setSelectedClassroomId(classroom.classroomId),
        columnText: classroom.leadTeacherFirstName || '',
      },
      {
        columnName: 'leadTeacherLastName',
        columnOnClick: () => setSelectedClassroomId(classroom.classroomId),
        columnText: classroom.leadTeacherLastName || '',
      }
    );
    return rowCells;
  });

  const getTeacherHeaderData = () => [
    { label: t('placementSelectColSelect', 'Select'), filterKey: '', sortKey: '' },
    {
      filterAutoCompleteCallback: null,
      filterComparator: 'contains',
      filterKey: 'lastName',
      label: t('placementSelectTeacherColLastName', 'Teacher Last Name'),
      sortKey: 'lastName',
    },
    {
      filterAutoCompleteCallback: null,
      filterComparator: 'contains',
      filterKey: 'firstName',
      label: t('placementSelectTeacherColFirstName', 'Teacher First Name'),
      sortKey: 'firstName',
    },
  ];

  const getTeacherBodyData = () => teacherUsers.map((teacher) => {
    const isSelected = selectedTeacherId === teacher.id;
    return [
    {
      columnName: 'select',
      columnOnClick: () => setSelectedTeacherId(teacher.id),
      // eslint-disable-next-line react/react-in-jsx-scope
      columnText: <input readOnly checked={isSelected} type='radio' />,
    },
    {
      columnName: 'lastName',
      columnOnClick: () => setSelectedTeacherId(teacher.id),
      columnText: teacher.lastName || '',
    },
    {
      columnName: 'firstName',
      columnOnClick: () => setSelectedTeacherId(teacher.id),
      columnText: teacher.firstName || '',
    },
  ];
  });

  /* ── renderers ── */
  const renderSelectStep = () => {
    const tableData = filteredHeaderTableManager.getTableData(CLASSROOM_TABLE_ID);
    const sortColumn = tableData?.column || '';
    const sortDirection = tableData?.direction || 'ascending';
    const filters = tableData?.filters || [];

    return (
      <>
        <Modal.Content>
          {!displayedClassroomsLoaded ? (
            <SatCoreLoader height='400px' t={t} />
          ) : (
            <>
              {displayedTotalPages > 1 && (
                <div className='placement-classroom-table-controls'>
                  <Pagination
                    activePage={currentPage}
                    firstItem={{ content: '<<', icon: false }}
                    lastItem={{ content: '>>', icon: false }}
                    nextItem={{ content: '>', icon: false }}
                    onPageChange={handleClassroomPageChange}
                    prevItem={{ content: '<', icon: false }}
                    totalPages={displayedTotalPages} />
                </div>
              )}
              <div className='placement-classroom-reset-filters'>
                <span
                  className='placement-classroom-reset-link'
                  onClick={handleResetClassroomFilters}
                  role='button'
                  tabIndex={0}>
                  {t('placementAddToClassroomResetFilters', 'Reset Filters')}
                </span>
              </div>
              <FilteredHeaderTable
                column={sortColumn}
                direction={sortDirection}
                filtersData={filters}
                handleFilter={handleClassroomFilter}
                handleSort={handleClassroomSort}
                tableBodyData={getClassroomBodyData()}
                tableHeaderData={getClassroomHeaderData()}
                tableId={CLASSROOM_TABLE_ID} />
              <div className='placement-classroom-add-btn-wrapper'>
                <Button className='btn-add-classroom' onClick={handleAddClassroom} primary type='button'>
                  {t('placementAddToClassroomAddClassroomButton', 'Create a Classroom')}
                </Button>
              </div>
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button basic className='cancelButton' onClick={onClose} primary>
            {t('placementAddToClassroomCancelButton', 'Cancel')}
          </Button>
          <Button disabled={!selectedClassroomId} onClick={handleNext} primary>
            {t('placementAddToClassroomNextButton', 'Next')}
          </Button>
        </Modal.Actions>
      </>
    );
  };

  const renderSelectTeacherStep = () => {
    const tableData = filteredHeaderTableManager.getTableData(TEACHER_TABLE_ID);
    const sortColumn = tableData?.column || '';
    const sortDirection = tableData?.direction || 'ascending';
    const filters = tableData?.filters || [];

    return (
      <>
        <Modal.Content>
          {!teachersLoaded ? (
            <SatCoreLoader height='400px' t={t} />
          ) : (
            <>
              {teacherTotalPages > 1 && (
                <div className='placement-classroom-table-controls'>
                  <Pagination
                    activePage={teacherPage}
                    firstItem={{ content: '<<', icon: false }}
                    lastItem={{ content: '>>', icon: false }}
                    nextItem={{ content: '>', icon: false }}
                    onPageChange={handleTeacherPageChange}
                    prevItem={{ content: '<', icon: false }}
                    totalPages={teacherTotalPages} />
                </div>
              )}
              <div className='placement-classroom-reset-filters'>
                <span
                  className='placement-classroom-reset-link'
                  onClick={handleResetTeacherFilters}
                  role='button'
                  tabIndex={0}>
                  {t('placementAddToClassroomResetFilters', 'Reset Filters')}
                </span>
              </div>
              <FilteredHeaderTable
                column={sortColumn}
                direction={sortDirection}
                filtersData={filters}
                handleFilter={handleTeacherFilter}
                handleSort={handleTeacherSort}
                tableBodyData={getTeacherBodyData()}
                tableHeaderData={getTeacherHeaderData()}
                tableId={TEACHER_TABLE_ID} />
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button basic className='cancelButton' onClick={handleTeacherBack} primary>
            {t('placementAddToClassroomCancelButton', 'Cancel')}
          </Button>
          <Button disabled={!selectedTeacherId} onClick={handleTeacherNext} primary>
            {t('placementAddToClassroomNextButton', 'Next')}
          </Button>
        </Modal.Actions>
      </>
    );
  };

  const renderCreateClassStep = () => {
    const institutionOptions = Array.from(userManager.institutionsMap.values()).map((inst) => ({
      key: inst.id,
      text: inst.name,
      value: inst.id,
    }));

    return (
      <>
        <Modal.Content>
          <Form className='placement-create-class-form'>
            <Form.Field required>
              <label>{t('placementCreateClassNameLabel', 'Class Name')}</label>
              <Form.Input
                onChange={(_e, { value }) => {
                  setClassName(value);
                  setClassFormErrors((prev) => ({ ...prev, className: false }));
                }}
                placeholder={t('placementCreateClassNamePlaceholder', 'Class Name')}
                value={className} />
              <Message
                content={t('placementCreateClassNameRequired', 'Class name is required.')}
                error
                visible={!!classFormErrors.className} />
            </Form.Field>
            {institutionOptions.length > 1 && (
              <Form.Field required>
                <label>{t('placementCreateClassInstitutionLabel', 'Select Institution')}</label>
                <Dropdown
                  onChange={(_e, { value }) => setClassInstitutionId(value)}
                  options={institutionOptions}
                  placeholder={t('placementCreateClassInstitutionPlaceholder', 'Institution')}
                  selection
                  value={classInstitutionId} />
              </Form.Field>
            )}
            <Form.Field required>
              <DateInput
                animation='false'
                className='date-input'
                dateFormat='MM/DD/YYYY'
                iconPosition='left'
                label={t('placementCreateClassStartDateLabel', 'Start Date')}
                name='classStartDate'
                onChange={(_e, { value }) => {
                  setClassStartDate(value);
                  setClassFormErrors((prev) => ({ ...prev, classStartDate: false }));
                }}
                placeholder='MM/DD/YYYY'
                value={classStartDate} />
              <Message
                content={t('placementCreateClassStartDateRequired', 'Start date is required.')}
                error
                visible={!!classFormErrors.classStartDate} />
            </Form.Field>
            <Form.Field required>
              <DateInput
                animation='false'
                className='date-input'
                dateFormat='MM/DD/YYYY'
                iconPosition='left'
                label={t('placementCreateClassEndDateLabel', 'End Date')}
                name='classEndDate'
                onChange={(_e, { value }) => {
                  setClassEndDate(value);
                  setClassFormErrors((prev) => ({ ...prev, classEndDate: false }));
                }}
                placeholder='MM/DD/YYYY'
                value={classEndDate} />
              <Message
                content={t('placementCreateClassEndDateRequired', 'End date is required.')}
                error
                visible={!!classFormErrors.classEndDate} />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button basic className='cancelButton' onClick={handleCreateClassBack} primary>
            {t('placementAddToClassroomCancelButton', 'Cancel')}
          </Button>
          <Button onClick={handleCreateClassSubmit} primary>
            {t('placementAddToClassroomNextButton', 'Next')}
          </Button>
        </Modal.Actions>
      </>
    );
  };

  const renderConfirmStep = () => (
    <>
      <Modal.Content>
        <div className='placement-confirm-content'>
          <div className='placement-confirm-row'>
            <span className='placement-confirm-label'>
              {t('placementAddToClassroomConfirmClassroomLabel', 'Classroom:')}
            </span>
            <span className='placement-confirm-value'>
              {confirmMode === 'create' ? className : selectedClassroom?.classroomName}
            </span>
          </div>
          {(() => {
            let teacherName = null;
            if (confirmMode === 'add') {
              const fn = selectedClassroom?.leadTeacherFirstName;
              const ln = selectedClassroom?.leadTeacherLastName;
              teacherName = (fn || ln) ? `${fn || ''} ${ln || ''}`.trim() : (selectedClassroom?.leadTeacherName || null);
            } else if (createMode === 'forTeacher' && selectedTeacher) {
              teacherName = `${selectedTeacher.firstName || ''} ${selectedTeacher.lastName || ''}`.trim();
            } else if (createMode === 'forSelf') {
              teacherName = `${userManager.firstName || ''} ${userManager.lastName || ''}`.trim();
            }
            return teacherName ? (
              <div className='placement-confirm-row'>
                <span className='placement-confirm-label'>
                  {t('placementAddToClassroomConfirmLeadTeacherLabel', 'Lead Teacher:')}
                </span>
                <span className='placement-confirm-value'>{teacherName}</span>
              </div>
            ) : null;
          })()}
          <div className='placement-confirm-row'>
            <span className='placement-confirm-label'>
              {t('placementAddToClassroomConfirmStudentsLabel', 'Students:')}
            </span>
            <span className='placement-confirm-value'>
              {checkedInstances.map((instance) => (
                <span key={instance.userId} className='placement-confirm-student'>
                  {`${instance.firstName} ${instance.lastName}`}
                </span>
              ))}
            </span>
          </div>
          {submitError && (
            <div className='placement-confirm-error'>{submitError}</div>
          )}
        </div>
      </Modal.Content>
      <Modal.Actions>
        <Button basic className='cancelButton' disabled={submitting} onClick={handleBack} primary>
          {t('placementAddToClassroomBackButton', 'Back')}
        </Button>
        <Button disabled={submitting} loading={submitting} onClick={handleOk} primary>
          {t('placementAddToClassroomOkButton', 'Ok')}
        </Button>
      </Modal.Actions>
    </>
  );

  const titles = {
    [STEP.SELECT]: t('placementAddToClassroomModalTitle', 'Select/Add Classroom'),
    [STEP.SELECT_TEACHER]: t('placementSelectTeacherTitle', 'Select Teacher for Classroom'),
    [STEP.CREATE_CLASS]: t('placementCreateClassTitle', 'Add Class'),
    [STEP.CONFIRM]: t('placementAddToClassroomConfirmTitle', 'Confirm Selection'),
  };

  return (
    <Modal
      className={classNames('GradebookPlacementAddToClassroomModal', {
        'step-small': step === STEP.CONFIRM || step === STEP.CREATE_CLASS,
      })}
      closeOnDimmerClick={false}
      closeOnEscape={!submitting}
      onClose={onClose}
      open={open}
      size='large'>
      <ModalBanner onClose={onClose} title={titles[step]} />
      {step === STEP.SELECT && renderSelectStep()}
      {step === STEP.SELECT_TEACHER && renderSelectTeacherStep()}
      {step === STEP.CREATE_CLASS && renderCreateClassStep()}
      {step === STEP.CONFIRM && renderConfirmStep()}
    </Modal>
  );
});

export default GradebookPlacementAddToClassroomModal;

SatCoreRegister('GradebookPlacementAddToClassroomModal', GradebookPlacementAddToClassroomModal);
